/**
 * Draft state for the free-text conversation filters.
 *
 * Kept free of React so the debounce rules can be unit tested directly.
 */
import type { ConversationFilters } from "../types";

/**
 * Filters that change on every keystroke and so are debounced before they
 * reach the API. Selects and quick-filter chips are a single deliberate
 * action and apply immediately.
 */
export const DEBOUNCED_FIELDS = ["search", "username", "minTimeMs", "maxTimeMs"] as const;

export type DebouncedField = (typeof DEBOUNCED_FIELDS)[number];

/** The typed-but-not-yet-applied values behind the free-text inputs. */
export type FilterDraft = Record<DebouncedField, string>;

/** How long typing has to pause before the list refetches. */
export const FILTER_DEBOUNCE_MS = 300;

export const EMPTY_DRAFT: FilterDraft = {
  search: "",
  username: "",
  minTimeMs: "",
  maxTimeMs: "",
};

/**
 * Fold the draft text values into the applied filter set.
 *
 * Returns the previous object unchanged when nothing actually differs, so a
 * debounce timer firing after an unrelated re-render cannot trigger a
 * refetch: the fetch effect keys off the filters object's identity.
 */
export function mergeDraft(prev: ConversationFilters, draft: FilterDraft): ConversationFilters {
  const changed = DEBOUNCED_FIELDS.some((field) => String(prev[field] ?? "") !== draft[field]);
  if (!changed) return prev;
  // Any filter change invalidates the current page offset.
  return { ...prev, ...draft, page: 1 };
}
